import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
} from "@angular/core";
import { encode } from "uqr";

/**
 * Renders a QR code as inline SVG (one path for all dark modules). Prints
 * crisply at any size and needs no innerHTML.
 *
 *   <app-qr-code [value]="url" [size]="160" logo="/assets/img/logo-icon.svg"></app-qr-code>
 *
 * With a logo, error correction goes to level H (30% recoverable) and the
 * logo sits on a dark rounded tile with a white margin covering ~23% of the
 * width (~5% of the modules), well inside what scanners tolerate.
 */
@Component({
  selector: "app-qr-code",
  template: `
    @if (path) {
      <svg
        [attr.width]="size"
        [attr.height]="size"
        [attr.viewBox]="'0 0 ' + modules + ' ' + modules"
        shape-rendering="crispEdges"
        role="img"
        [attr.aria-label]="label"
      >
        <rect [attr.width]="modules" [attr.height]="modules" fill="#fff" />
        <path [attr.d]="path" fill="#000" />
        @if (logo) {
          <rect
            [attr.x]="tile.x - 1"
            [attr.y]="tile.y - 1"
            [attr.width]="tile.w + 2"
            [attr.height]="tile.w + 2"
            fill="#fff"
          />
          <rect
            [attr.x]="tile.x"
            [attr.y]="tile.y"
            [attr.width]="tile.w"
            [attr.height]="tile.w"
            [attr.rx]="tile.w * 0.18"
            fill="#242638"
            shape-rendering="geometricPrecision"
          />
          <image
            [attr.href]="logo"
            [attr.x]="tile.x + tile.w * 0.08"
            [attr.y]="tile.y + tile.w * 0.12"
            [attr.width]="tile.w * 0.84"
            [attr.height]="tile.w * 0.84"
          />
        }
      </svg>
    }
  `,
  styles: [
    `
      :host {
        display: inline-block;
        line-height: 0;
      }
      svg {
        display: block;
        max-width: 100%;
        height: auto;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QrCodeComponent implements OnChanges {
  @Input() value = "";
  @Input() size = 160;
  @Input() label = "QR code";
  /** Optional image URL drawn on a dark tile in the center. */
  @Input() logo?: string;

  modules = 0;
  path = "";
  tile = { x: 0, y: 0, w: 0 };

  ngOnChanges(): void {
    if (!this.value) {
      this.path = "";
      this.modules = 0;
      return;
    }
    const qr = encode(this.value, {
      ecc: this.logo ? "H" : "M",
      border: 2,
    });
    this.modules = qr.size;
    const parts: string[] = [];
    // Skip modules under the logo tile so nothing shows through the rounded
    // corners; the tile itself is what the scanner treats as damage.
    const w = this.logo ? Math.round(qr.size * 0.23) : 0;
    const x = Math.floor((qr.size - w) / 2);
    this.tile = { x, y: x, w };
    qr.data.forEach((row, py) => {
      row.forEach((dark, px) => {
        if (!dark) return;
        if (w && px >= x - 1 && px < x + w + 1 && py >= x - 1 && py < x + w + 1)
          return;
        parts.push(`M${px} ${py}h1v1h-1z`);
      });
    });
    this.path = parts.join("");
  }
}
